import React, { useState } from 'react';
import { CheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import { useSettingsDialog } from '@/contexts/SettingsDialogContext';

const COLUMNS = [
  'Payer Policy Pulse',
  'Top Headlines',
  'Trending in RCM Research',
  'Denial Watch',
] as const;

const STEPS = [
  {
    title: 'Add a custom MCP connector',
    body: 'In ChatGPT, create a custom connector and choose MCP. In Claude, open Customize, then Connectors, and add a custom connector. When Claude asks how it should sign in, choose "Use Claude\'s published identity." On a Team or Enterprise plan, an Owner adds the connector before anyone else can connect.',
  },
  {
    title: 'Paste the server address',
    body: 'ChatGPT and Claude use this address to load your profile and the four column prompts.',
  },
  {
    title: 'Sign in with your RCM Library account',
    body: 'When the connector asks you to connect, use the same account you use here. The desk is then limited to your profile.',
  },
  {
    title: 'Ask it to run RCM Desk',
    body: 'The chat writes the four columns from those prompts and your saved profile. RCM Desk does not publish them on this page.',
  },
] as const;

/** Address ChatGPT and Claude should use for the Desk MCP server. */
export function deskMcpServerUrl(fallbackOrigin?: string): string {
  const configured = import.meta.env.VITE_MCP_PUBLIC_ORIGIN?.trim().replace(/\/$/, '');
  if (configured) return `${configured}/mcp`;
  const api = import.meta.env.VITE_API_URL?.trim().replace(/\/$/, '');
  if (api && /^https?:\/\//.test(api)) return `${api}/mcp`;
  // Vite dev does not proxy /mcp. The local façade listens on the API port.
  if (import.meta.env.DEV) return 'http://localhost:8000/mcp';
  const origin = (fallbackOrigin ?? (typeof window === 'undefined' ? '' : window.location.origin))
    .trim()
    .replace(/\/$/, '');
  return origin ? `${origin}/mcp` : '/mcp';
}

export function DeskPage() {
  const { openSettings } = useSettingsDialog();
  const serverUrl = deskMcpServerUrl();
  const [copied, setCopied] = useState(false);

  async function copyAddress() {
    try {
      await navigator.clipboard.writeText(serverUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="rcm-desk flex-1 overflow-y-auto container-scroll">
      <div className="mx-auto max-w-3xl px-5 py-8 md:px-8 md:py-12">
        <p className="rcm-desk-section-label text-xs font-semibold tracking-[0.14em]">RCM DESK</p>
        <h1 className="font-display mt-2 text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          Run your desk in ChatGPT or Claude
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
          Payer policy, headlines, research, and denial watch are written in ChatGPT or Claude.
          This page does not publish them. The connector reads your profile and our column prompts,
          then the chat writes the columns.
        </p>

        <ol className="mt-8 space-y-3">
          {STEPS.map((step, index) => (
            <li
              key={step.title}
              className="rounded-xl border border-border bg-card px-4 py-4 md:px-5"
            >
              <div className="flex gap-4">
                <span className="rcm-desk-num font-display w-8 flex-shrink-0 text-xl font-bold leading-none tabular-nums">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="font-display text-base font-semibold tracking-tight text-foreground">
                    {step.title}
                  </h2>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{step.body}</p>
                  {index === 1 ? (
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                      <code
                        id="desk-mcp-url"
                        className="min-w-0 flex-1 overflow-x-auto rounded-lg border border-border bg-[hsl(var(--secondary)/0.55)] px-3 py-2 font-mono text-sm text-foreground"
                      >
                        {serverUrl}
                      </code>
                      <Button
                        type="button"
                        variant="outline"
                        className="shrink-0"
                        onClick={copyAddress}
                      >
                        {copied ? (
                          <CheckIcon className="mr-1.5 h-4 w-4" aria-hidden />
                        ) : (
                          <ClipboardDocumentIcon className="mr-1.5 h-4 w-4" aria-hidden />
                        )}
                        {copied ? 'Copied' : 'Copy address'}
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ol>

        <section className="mt-8 rounded-xl border border-border bg-[hsl(var(--secondary)/0.45)] px-4 py-4 md:px-5">
          <h2 className="font-display text-sm font-semibold tracking-tight text-foreground">
            Columns the connector writes
          </h2>
          <ul className="mt-3 flex flex-wrap gap-2">
            {COLUMNS.map((name) => (
              <li
                key={name}
                className="rounded-full border border-border bg-card px-3 py-1 text-sm text-foreground"
              >
                {name}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Your saved profile — payer focus, state, care setting, and the rest — shapes every
            column. Keep it current before you ask ChatGPT or Claude to run the desk.
          </p>
          <Button
            type="button"
            variant="outline"
            className="mt-3"
            onClick={() => openSettings('profile')}
          >
            Review your profile
          </Button>
        </section>
      </div>
    </div>
  );
}
